import React, { useState, CSSProperties, useEffect, useRef } from 'react'
import styled from 'styled-components'
import ReactECharts from 'components/ReactECharts'
import { color, type EChartsOption, type SetOptionOpts } from 'echarts'
import mapImage from '../../images/dialekt-map-127-voronoi.svg'

export interface ReactEChartsProps {
  option: EChartsOption
  style?: CSSProperties
  settings?: SetOptionOpts
  loading?: boolean
  theme?: string
  data: {}
}
type ChartRefType = HTMLDivElement & {
  closeTooltip?: () => void
}

const ColorScale = ({
  tenant,
  colorMode,
  data,
  isBig,
  showScale,
  min,
  max,
  minColor,
  midColor,
  maxColor,
  minTerm,
  maxTerm,
}: {
  tenant?: object
  colorMode?: string
  data: any
  isBig?: boolean
  showScale?: boolean
  min?: number
  max?: number
  minColor: string
  midColor?: string
  maxColor: string
  minTerm?: string
  maxTerm?: string
}) => {
  const [isMobile, setIsMobile] = useState(false)
  const [chartOptions, setChartOptions] = useState({})

  const chartRef = useRef<ChartRefType>(null)

  const handleResize = (): void => {
    setIsMobile(window.innerWidth < 600)
  }

  useEffect(() => {
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])
  useEffect(() => {
    setIsMobile(window.innerWidth < 600)
  }, [])

  const commatize = (word: any) => {
    const replaced = word.toString().replace('.', ',')
    return replaced.indexOf(',0') > 1 && replaced.slice(-2) === ','
      ? replaced.replace(',0', '')
      : replaced
  }

  // echarts options
  useEffect(() => {
    // prepare data for display
    // console.log('data', data)

    // helper function to interpolate color
    const interpolateColor = function (
      color1: string,
      color2: string,
      factor: number,
      min: number,
      max: number,
      value: number
    ) {
      if (value >= max) {
        return color2
      } else if (value <= min) {
        return color1
      }
      const color1RGB = parseInt(color1.slice(1), 16)
      const r1 = color1RGB >> 16
      const g1 = (color1RGB >> 8) & 0xff
      const b1 = color1RGB & 0xff

      const color2RGB = parseInt(color2.slice(1), 16)
      const r2 = color2RGB >> 16
      const g2 = (color2RGB >> 8) & 0xff
      const b2 = color2RGB & 0xff

      const r = Math.round(r1 + factor * (r2 - r1))
      const g = Math.round(g1 + factor * (g2 - g1))
      const b = Math.round(b1 + factor * (b2 - b1))

      return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)
    }

    // get min and max values for color interpolation
    let minValue = 0
    let maxValue = 0
    let midValue = 0
    // console.log('min and max', min, max)
    if (min !== undefined && max !== undefined) {
      minValue = min
      maxValue = max
      midValue = (minValue + maxValue) / 2
    } else {
      minValue = Math.min(...data.map((item: any) => item.smoothed))
      maxValue = Math.max(...data.map((item: any) => item.smoothed))
      midValue = (minValue + maxValue) / 2
    }

    const regionsData = data.map((item: any) => {
      let factor = -1
      let color = ''

      if (midColor === undefined) {
        factor = (item.smoothed - minValue) / (maxValue - minValue)
        color = interpolateColor(
          minColor,
          maxColor,
          factor,
          minValue,
          maxValue,
          item.smoothed
        )
      } else {
        if (item.smoothed <= midValue) {
          factor = (item.smoothed - minValue) / (midValue - minValue)
          color = interpolateColor(
            minColor,
            midColor,
            factor,
            minValue,
            maxValue,
            item.smoothed
          )
        } else {
          factor = (item.smoothed - midValue) / (maxValue - midValue)
          color = interpolateColor(
            midColor,
            maxColor,
            factor,
            minValue,
            maxValue,
            item.smoothed
          )
        }
      }
      return {
        name: item.site_code,
        silent: true,
        itemStyle: {
          color: color,
          borderColor: color,
          borderWidth: 1,
        },
      }
    })

    // echart options
    const chartOptions: ReactEChartsProps['option'] = {
      visualMap: {
        show: true,
        type: 'continuous', // 'piecewise',
        precision: 2,
        left: 'center',
        bottom: -5,
        orient: 'horizontal',
        min: minValue,
        max: maxValue,
        calculable: false,
        inRange: {
          color: midColor
            ? [minColor, midColor, maxColor]
            : [minColor, maxColor],
        },
        formatter: function (value) {
          // Customize the text here
          const perMinute = 60 * (value as number)
          return Math.round(perMinute) + ' Silben pro Minute'
        },
        text: [maxTerm ? maxTerm : 'max', minTerm ? minTerm : 'min'],
        textStyle: {
          color: colorMode === 'dark' ? '#f4f4f4' : '#202346',
        },
      },
    }

    setChartOptions(chartOptions)
  }, [data, isMobile])

  return (
    <Scale className={isMobile ? 'mobile' : 'desktop'}>
      <div className='graphic'>
        <ReactECharts
          ref={chartRef}
          mapImage={mapImage}
          option={chartOptions}
          theme={colorMode === 'dark' ? 'ta-dark' : 'ta'}
          height={window.innerWidth < 600 ? '40px' : '40px'}
          onClicked={(e) => {
            console.log('clicked', e)
          }}
        ></ReactECharts>
      </div>
    </Scale>
  )
}
const Scale = styled.div`
  color: var(--text-color);
  width: 100%;
  height: auto;
  margin: -2rem 0 4rem;
`

export default ColorScale
